<template>
<div>

    <v-sheet class="pa-3 mb-4" color="grey lighten-5" elevation="0" rounded>
        <div class="d-flex align-center">
            <div>
                <p class="mb-1"><strong>Do not include shortened links in your message.</strong> Always enter the full URL.</p>
                <p class="mb-1">Public shorteners (Bitly, TinyURL, Rebrandly, etc.) violate carrier rules and can cause up to 90% of messages to be blocked.</p>
                <p class="mb-0">If RedChirp link shortening is enabled, links will be shortened automatically when the message is sent.</p>
            </div>
        </div>
    </v-sheet>

    <v-alert
        v-if="shortened_links_detected"
        border="left"
        colored-border
        color="red darken-2"
        elevation="2"
        type="error"
        prominent
    >
    
        <strong>Warning:</strong> Shortened URLs detected in your message. Please use full URLs to avoid your message being marked as spam.
        Suspected shortened link: {{ shortened_link }}
    </v-alert>


    <p><b>Message Pattern(s)</b></p>

    <p
        v-if="!show_help"
    >
        <a
            v-on:click="show_help = true"
        >
            How do message patterns work?
        </a>
    </p>
    <p
        v-else
    >
        <b>How do message patterns work?</b>
    </p>
    <div
        v-if="show_help"
    >
        <p>Message patterns contain variables inside of curly braces.  For example <code>{first-name}</code> will be replaced with the recipient's first name.</p>
        <p>Sometimes, a variable will not have a value.  For example, if we attempt to send a message to someone where RedChirp does not know the recipient's first name, it will not be possible to replace the <code>{first-name}</code> variable with the recipients first name.  When a variable is missing, RedChirp will try to use the next message pattern.  So, by providing multiple message pattern(s) you can provide an alternate message for those occasions where a variable is missing.</p>
        <p>You can test your message patterns for the presence or absence of variables using the chips below.  When a chip looks like this:</p>
        <p><v-chip class="chip_on" close>{first-name}</v-chip></p>
        <p>It means that the variable is present.  For example, the above chip means that the <code>{first-name}</code> variable has a value.  Clicking on a green chip will turn it gray:</p>
        <p><v-chip class="chip_off">{first-name}</v-chip></p>
        <p>Which means that the variable is not present.</p>
    </div>

    <div
        v-for="(pattern_value, pattern_index) in value_pretty"
        :key="pattern_index"
        class="message-pattern-row"
    >
        <message-pattern-textarea
            :ref="'pattern-' + pattern_index"
            :value="pattern_value"
            :variables="variables"
            @input="onInput(pattern_index, $event)"
            @focus="onPatternFocus(pattern_index)"
        />
        <span class="message-pattern-actions">
            <emoji-picker-compact
                @select="onEmojiSelect($event, pattern_value, pattern_index)"
            />

            <v-menu
                bottom
                offset-y
            >
                <template v-slot:activator="{ on }">
                    <v-icon
                        size="25"
                        v-on="on"
                    >
                        mdi-dots-vertical
                    </v-icon>
                </template>

                <v-list>
                    <v-list-item
                        :disabled="pattern_index == 0"
                        @click="onClickMoveUp(pattern_index)"
                    >
                        <v-list-item-title>Move Up</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        :disabled="pattern_index == value_pretty.length - 1"
                        @click="onClickMoveDown(pattern_index)"
                    >
                        <v-list-item-title>Move Down</v-list-item-title>
                    </v-list-item>

                    <v-divider/>

                    <v-list-item
                        @click="onClickDelete(pattern_index)"
                    >
                        <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </span>
    </div>

    <!-- Shared variable chips for all patterns -->
    <div v-if="variables.length > 0" class="shared-variable-chips">
        <span class="chips-label">Insert variable:</span>
        <v-chip
            v-for="variable in variables"
            :key="'insert-' + variable.name"
            small
            outlined
            color="primary"
            class="variable-chip"
            @click="insertVariableIntoFocusedPattern(variable.name)"
        >
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                        {{"{"}}{{ variable.name }}{{"}"}}
                    </span>
                </template>
                <span>{{ variable.description }}</span>
            </v-tooltip>
        </v-chip>
    </div>

    <div
        style="text-align: right"
    >
        <v-btn
            @click="onClickVariables"
            style="margin-right: 15px;"
        >
            All Variables...
        </v-btn>

        <v-btn
            @click="onClickAddPattern"
        >
            Add Pattern
        </v-btn>
    </div>

    <b>Test your message:</b>

    <div
        style="margin-top: 15px; margin-bottom: -15px"
    >
    <v-chip
        v-for="(item, index) in variables"
        :key="'cb_chip_'+index"
        :class="{'chip_on': enabled_variables.includes(item.name), 'chip_off': !enabled_variables.includes(item.name)}"
        :close="enabled_variables.includes(item.name)"
        v-on:click="onClickVariableChip(item)"
        v-on:click:close="onClickVariableChip(item)"
    >
        {{'{'+item.name+'}'}}
    </v-chip>
    </div>




    <v-textarea
        style="margin-top: 25px;"
        label="Example Message"
        :value="example_message"
        readonly
        rows="1"
        auto-grow
    >
         <span
             slot="append-outer"
        >
            <v-icon
                size="25"
                v-on:click="example_index++; updateExampleMessage()"
            >
                mdi-refresh
            </v-icon>
         </span>
    </v-textarea>

    {{example_sms_message}}

    <m-edit-message-patterns-variable-dialog
        ref="variables-dialog"
        :context="context"
        :bulk_message_id="bulk_message_id"
    />



</div>
</template>

<script>
const _ = require('lodash');
import MessagePatternTextarea from './message-pattern-textarea.vue';
import EmojiPickerCompact from './emoji-picker-compact.vue';

export default {
    components: {
        MessagePatternTextarea,
        EmojiPickerCompact,
    },
    props: {
        context: {
            type: String,
            default: function() { return 'manually-sent'; }
        },

        value: {
            type: Array,
            default: function() { return []; }
        },

        always_mms: {
            type: Boolean,
            default: function() { return false; }
        },

        bulk_message_id: {
            type: String,
            default: function() { return null; }
        },

        trigger_save: {
            type: Boolean,
            default: function() { return false; }
        },
    },

    data() {
        return {
            variables: [],
            enabled_variables: [],
            example_message: null,
            example_index: 0,
            picker_style: null,
            show_help: false,
            shortened_links_detected: false,
            shortened_link: null,
            debounce_timer: null,
            focused_pattern_index: 0,
        }
    },

    mounted () {
        this.update(true);
    },

    beforeDestroy() {
        // Clean up timer
        if (this.debounce_timer) {
            clearTimeout(this.debounce_timer);
        }
    },

    methods: {
        update(set_enabled_vars)
        {
            if ( this.context == null ) return;

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/message-patterns/variables',

                'params' : {
                        'context': this.context,
                        'bulk_message_id': this.bulk_message_id,
                    },

                'on_success': (data) => {
                        this.variables = data.results;

                        if ( set_enabled_vars )
                        {
                            let ev = [];

                            for ( let i = 0; i < this.variables.length; i++ )
                            {
                                ev.push(this.variables[i].name);
                            }

                            this.enabled_variables = ev;
                        }
                    },

                'on_error': 'Unable to complete API call: could not get message patterns for context: '+this.context,
            });
        },

        onClickVariableChip(item)
        {
            let found = false;
            let ret = [];

            for ( let i = 0; i < this.enabled_variables.length; i++ )
            {
                if( this.enabled_variables[i] == item.name )
                {
                    found = true;
                    continue;
                }

                ret.push(this.enabled_variables[i]);
            }

            if ( !found )
                ret.push(item.name);

            this.enabled_variables =  ret;
        },

        updateExampleMessage()
        {
            // Clear existing timer
            if (this.debounce_timer) {
                clearTimeout(this.debounce_timer);
            }

            // Set new timer with 500ms delay
            this.debounce_timer = setTimeout(() => {
                this.performUpdateExampleMessage();
            }, 500);
        },

        performUpdateExampleMessage()
        {
            // Don't make API call if patterns are empty or all blank
            if (!this.value || this.value.length === 0 || this.value.every(pattern => !pattern || pattern.trim().length === 0)) {
                this.example_message = 'Could not generate a message';
                this.$emit('example-message-changed', this.example_message);
                return;
            }

            let params = {
                'context' : this.context,
                'patterns' : this.value,
                'enabled_variables' : this.enabled_variables,
                'index' : this.example_index,
                'bulk_message_id': this.bulk_message_id,
            };

            // Single request: get both example and shortened link info from TEST: /api/v1/message-patterns
            // Backend now includes shortened link detection in the test response to avoid a second call
            this.$rc.testMessagePatterns(
                params,
                (data) => {
                    if ( data.example_message == null )
                        data.example_message = 'Could not generate a message';

                    this.example_message = data.example_message;
                    this.$emit('example-message-changed', this.example_message);

                    // Update shortened link UI when provided by backend
                    if ( data.shortened_links_detected != null )
                        this.shortened_links_detected = !!data.shortened_links_detected;
                    if ( data.first_shortened_url != null )
                        this.shortened_link = data.first_shortened_url;
                },

                (error) => {
                    this.example_message = 'Could not generate a message';
                    this.$emit('example-message-changed', this.example_message);
                },
            );
        },

        onInput(index, text)
        {
            let ret = _.cloneDeep(this.value_pretty);
            if ( ret == null ) ret = [];

            while(index >= ret.length)
                ret.push('');

            ret[index] = text;

            this.$emit('input', ret);
        },

        onClickAddPattern()
        {
            let ret = _.cloneDeep(this.value_pretty);
            if ( ret == null ) ret = [];
            ret.push('');

            this.$emit('input', ret);
        },

        onClickDelete(index)
        {
            let ret = _.cloneDeep(this.value_pretty);
            if ( ret == null ) ret = [];
            ret.splice(index,1);
            this.$emit('input', ret);
        },

        onClickMoveUp(index)
        {
            let ret = _.cloneDeep(this.value_pretty);
            if ( ret == null ) ret = [];

            let tmp = ret[index];
            ret[index] = ret[index-1];
            ret[index-1] = tmp;

            this.$emit('input', ret);
        },

        onClickMoveDown(index)
        {
            let ret = _.cloneDeep(this.value_pretty);
            if ( ret == null ) ret = [];

            let tmp = ret[index];
            ret[index] = ret[index+1];
            ret[index+1] = tmp;

            this.$emit('input', ret);
        },

        onClickVariables()
        {
            this.$refs['variables-dialog'].showDialog();
        },

        onEmojiSelect(emoji, pattern_value, pattern_index)
        {
            // Insert emoji into the pattern at the cursor position
            const ref_name = 'pattern-' + pattern_index;
            const pattern_refs = this.$refs[ref_name];
            
            if (pattern_refs && pattern_refs.length > 0) {
                const pattern_component = pattern_refs[0];
                if (pattern_component.insertEmojiAtCursor) {
                    // Use the new method that inserts at cursor position
                    pattern_component.insertEmojiAtCursor(emoji);
                } else {
                    // Fallback: append to end
                    if (pattern_value == null || pattern_value.trim().length == 0) {
                        this.onInput(pattern_index, emoji);
                    } else {
                        this.onInput(pattern_index, pattern_value + ' ' + emoji);
                    }
                }
            } else {
                // Fallback if ref not found
                if (pattern_value == null || pattern_value.trim().length == 0) {
                    this.onInput(pattern_index, emoji);
                } else {
                    this.onInput(pattern_index, pattern_value + ' ' + emoji);
                }
            }
        },

        onPatternFocus(pattern_index)
        {
            this.focused_pattern_index = pattern_index;
        },

        insertVariableIntoFocusedPattern(var_name)
        {
            const ref_name = 'pattern-' + this.focused_pattern_index;
            const pattern_refs = this.$refs[ref_name];
            
            if (pattern_refs && pattern_refs.length > 0) {
                const pattern_component = pattern_refs[0];
                // Use the component's insert method if available
                if (pattern_component.insertVariableAtEnd) {
                    pattern_component.insertVariableAtEnd(var_name);
                } else {
                    // Fallback: append to the pattern value
                    const current_value = this.value_pretty[this.focused_pattern_index] || '';
                    const separator = current_value.length > 0 && !current_value.endsWith(' ') ? ' ' : '';
                    const new_value = current_value + separator + '{' + var_name + '}';
                    this.onInput(this.focused_pattern_index, new_value);
                }
            } else {
                // Fallback if ref not found - insert into first pattern
                const current_value = this.value_pretty[0] || '';
                const separator = current_value.length > 0 && !current_value.endsWith(' ') ? ' ' : '';
                const new_value = current_value + separator + '{' + var_name + '}';
                this.onInput(0, new_value);
            }
        },

        // Method to emit save request with shortened link check
        requestSave()
        {
            if (!this.value || this.value.length === 0) {
                this.$emit('save-requested');
                return;
            }

            // Reuse TEST: /api/v1/message-patterns to include shortened link detection
            let params = {
                'context' : this.context,
                'patterns' : this.value,
                'enabled_variables' : this.enabled_variables,
                'index' : this.example_index,
                'bulk_message_id': this.bulk_message_id,
            };

            this.$rc.testMessagePatterns(
                params,
                (data) => {
                    if (data && data.shortened_links_detected)
                    {
                        this.showShortenedLinkConfirmation(data.first_shortened_url);
                    }
                    else
                    {
                        this.$emit('save-requested');
                    }
                },
                (error) => {
                    this.$emit('save-requested');
                },
            );
        },

        showShortenedLinkConfirmation(shortenedLink)
        {
            const bodyHtml = `
                <div style="margin-bottom: 20px;">
                    <div style="background-color: #ffebee; border: 1px solid #f44336; border-radius: 4px; padding: 16px; margin-bottom: 16px;">
                        <div style="font-weight: bold; color: #d32f2f; margin-bottom: 8px;">⚠️ High Spam Risk Warning</div>
                        <p style="margin-bottom: 8px;">
                            Your message contains shortened links, which are <strong>highly likely to be marked as spam</strong>
                            by carriers and may result in up to 90% of your messages being blocked.
                        </p>
                        <p style="margin-bottom: 8px;">
                            <strong>Detected shortened link:</strong> ${shortenedLink}
                        </p>
                        <p style="margin-bottom: 0;">
                            We strongly recommend using full URLs instead. If RedChirp link shortening is enabled,
                            links will be shortened automatically when the message is sent.
                        </p>
                    </div>
                </div>
            `;

            this.$rc.confirm({
                'title': 'Shortened Link Detected',
                'body': bodyHtml,
                'btn_text_ok': 'Save Anyway',
                'btn_text_cancel': 'Cancel',
                'btn_color_ok': 'error',
                // RedChirp standard confirm with input
                'text_to_confirm_message': 'Type "this could be marked as spam" to proceed',
                'text_to_confirm': 'this could be marked as spam',
                'on_ok': () => {
                    this.$emit('save-requested');
                },
            });
        },
    },

    watch: {
        context(newValue, oldValue) {
            this.update();
        },

        enabled_variables(newValue, oldValue) {
            this.updateExampleMessage();
        },

        value(newValue, oldValue) {
            this.updateExampleMessage();
        },

        trigger_save(newValue) {
            if (newValue) {
                this.requestSave();
                // Emit event to reset the trigger
                this.$emit('save-triggered');
            }
        },
    },

    computed: {
        value_pretty() {
            let value = this.value;
            if ( value == null ) return [''];
            if ( value.length == 0 ) return [''];
            return value;
        },

        example_sms_message() {
            let ex = this.example_message;
            if ( ex == null || ex == 'Could not generate a message' ) return '';

            return this.$rc.messageType(ex, this.always_mms);
        }
    },
}
</script>

<style lang="scss" scoped>
.message-pattern-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
}

.message-pattern-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding-top: 12px;
    min-width: 55px;
}

.shared-variable-chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 16px;
    padding: 10px;
    background: #fafafa;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.12);
}

.chips-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin-right: 4px;
}

.variable-chip {
    cursor: pointer;
    font-family: 'Roboto Mono', monospace;
    font-size: 12px;

    &:hover {
        background-color: rgba(25, 118, 210, 0.12) !important;
    }
}

.chip_on,
.chip_off
{
    margin-right: 15px;
    font-family: monospace;
    margin-bottom: 15px;
}

.chip_on
{
    background-color: rgba(0,255,0,0.3) !important;
}

.chip_off
{
    background-color: rgba(0,0,0,0.3);
}
</style>
